import { useSyncExternalStore } from "react";

import { legacyKeyOf, readWithLegacyFallback } from "./legacyStorage";

/**
 * Thème de l'interface (F4.4, P2-41). États : `light`, `dark`, `system`, plus
 * deux thèmes de marque `rose-astuce` et `rouge-transdev` (base ivoire, accent
 * de la charte). `system` suit `prefers-color-scheme` sans attribut ; tout autre
 * choix pose `data-theme` sur `<html>` (les jetons de theme.css basculent) et est
 * persisté par le navigateur.
 *
 * Le thème sombre vise le confort d'un poste occupé 8 h (CDC F4.4).
 */
export type ThemeChoice = "light" | "dark" | "system" | "rose-astuce" | "rouge-transdev";

const CHOICES: ThemeChoice[] = ["light", "dark", "system", "rose-astuce", "rouge-transdev"];

const KEY = "sentinelle.theme.v1";
const LEGACY_KEY = legacyKeyOf(KEY);
const listeners = new Set<() => void>();

function read(): ThemeChoice {
  const v = readWithLegacyFallback(KEY, LEGACY_KEY);
  if (v && (CHOICES as string[]).includes(v)) return v as ThemeChoice;
  return "system";
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);
}

export function setTheme(choice: ThemeChoice): void {
  try {
    if (choice === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* ignore */
  }
  applyTheme(choice);
  for (const l of [...listeners]) l();
}

/** À appeler une fois au démarrage (avant le premier rendu idéalement). */
export function initTheme(): void {
  applyTheme(read());
}

export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
    () => "system",
  );
}
